import type { UUID } from '@cmg/common';
import {
  Alert,
  Grid,
  LoadingButton,
  SectionContentSkeleton,
  SectionSkeleton,
  SnackbarManager,
} from '@cmg/design-system';
import { useIntl } from '@cmg/i18n';
import defaultTo from 'lodash/defaultTo';
import { Fragment, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';

import { useRoutes } from '../../../../../common/hooks/useRoutes/useRoutes';
import { GQLExtensionErrorAlert } from '../../../../../design-system/alert/GQLExtensionErrorAlert';
import { IdentityPageContent } from '../../../../../design-system/IdentityPageContent';
import { RolodexRecordStatus } from '../../../../../graphql';
import {
  LocalHighlights,
  LocalHighlightsSkeleton,
} from '../../crm-sales-coverage/components/LocalHighlights';
import { SECTIONS_SIZING } from '../../crm-sales-coverage/employee-detail/models/UserDetailsSection.model';
import { ContactInformationSection } from './components/ContactInformationSection';
import { FirmDetailsSection } from './components/FirmDetailsSection';
import { refetchFirmsSalesCoverageGridReactiveVar } from './components/FirmsSalesCoverageGrid/FirmsSalesCoverageGrid.model';
import { CMRInvestorFirmDetailGridsRoute } from './CRMInvestorFirmDetailGridsRoute';
import { useInvestorFirmDetailData } from './hooks/useInvestorFirmDetailData';
import { useUpdateInvestorFirm } from './hooks/useUpdateInvestorFirm';
import { mapInvestorFirmToUpdateInput } from './models/InvestorFirmDetail.model';

const FALLBACK_NAME = 'Unknown';

export function CMRInvestorFirmDetailRoute() {
  const { crmIntegrationId, firmId = '' } = useParams<{
    crmIntegrationId?: UUID;
    firmId: string;
  }>();

  const { crmInvestorFirms } = useRoutes(crmIntegrationId);

  const { firm, loading, error } = useInvestorFirmDetailData({
    firmId,
    tenantId: crmIntegrationId,
  });

  const breadcrumbs = useMemo(
    () => [
      { label: 'Investor Firms', href: crmInvestorFirms.getUrlPath({ crmIntegrationId }) },
      { label: defaultTo(firm?.name ?? firm?.firmKey, FALLBACK_NAME) },
    ],
    [crmIntegrationId, crmInvestorFirms, firm]
  );

  const { formatDate } = useIntl();

  const metaInfoLabels = useMemo(
    () => [
      {
        label: 'Created',
        value: `${formatDate(firm?.createdOn, { format: 'datetime' })} by ${
          firm?.createdByUserName ?? FALLBACK_NAME
        }`,
      },
      {
        label: 'Modified',
        value: `${formatDate(firm?.modifiedOn, { dateStyle: 'medium', timeStyle: 'short' })} by ${
          firm?.modifiedByUserName ?? FALLBACK_NAME
        }`,
      },
    ],
    [
      firm?.createdByUserName,
      firm?.createdOn,
      firm?.modifiedOn,
      firm?.modifiedByUserName,
      formatDate,
    ]
  );

  const { updateInvestorFirm } = useUpdateInvestorFirm(crmIntegrationId);

  const handleUpdateInvestorFirmsStatus = useCallback(
    async (status: RolodexRecordStatus) => {
      try {
        if (!firmId) {
          throw new Error('Firm ID is required');
        }

        if (!firm) {
          throw new Error('Firm data is required');
        }

        await updateInvestorFirm(firmId, mapInvestorFirmToUpdateInput(firm, status));
        refetchFirmsSalesCoverageGridReactiveVar(true);
        SnackbarManager.success('Firm status updated');
      } catch {
        SnackbarManager.error('Failed to update firm status');
      }
    },
    [firmId, firm, updateInvestorFirm]
  );

  const handleDisableFirmStatus = useCallback(() => {
    void handleUpdateInvestorFirmsStatus(RolodexRecordStatus.Deleted);
  }, [handleUpdateInvestorFirmsStatus]);

  const handleEnableFirmStatus = useCallback(() => {
    void handleUpdateInvestorFirmsStatus(RolodexRecordStatus.Effective);
  }, [handleUpdateInvestorFirmsStatus]);

  const actions = useMemo(() => {
    if (firm?.status === RolodexRecordStatus.Effective) {
      return [
        <LoadingButton
          key="disable-firm-button"
          variant="contained"
          color="error"
          onClick={handleDisableFirmStatus}
          loading={false}
        >
          Disable Firm
        </LoadingButton>,
      ];
    }

    return [
      <LoadingButton
        key="enable-firm-button"
        variant="outlined"
        onClick={handleEnableFirmStatus}
        loading={false}
      >
        Enable Firm
      </LoadingButton>,
    ];
  }, [firm?.status, handleDisableFirmStatus, handleEnableFirmStatus]);

  if (loading) {
    return (
      <IdentityPageContent
        localHighlights={<LocalHighlightsSkeleton />}
        gridContent={
          <Fragment>
            <Grid item {...SECTIONS_SIZING}>
              <SectionSkeleton aria-label="Loading firm details section">
                <SectionContentSkeleton />
              </SectionSkeleton>
            </Grid>
            <Grid item {...SECTIONS_SIZING}>
              <SectionSkeleton aria-label="Loading contact information section">
                <SectionContentSkeleton />
              </SectionSkeleton>
            </Grid>
          </Fragment>
        }
      />
    );
  }

  if (!firm) {
    return (
      <IdentityPageContent
        localHighlights={
          <LocalHighlights
            title={FALLBACK_NAME}
            breadcrumbs={breadcrumbs}
            metaInfoLabels={metaInfoLabels}
            actions={[]}
          />
        }
        gridContent={
          <Grid item>
            <Alert severity="error">Failed to load Investor Firm</Alert>
          </Grid>
        }
      />
    );
  }

  return (
    <IdentityPageContent
      localHighlights={
        <LocalHighlights
          title={firm.name ?? firm.firmKey}
          breadcrumbs={breadcrumbs}
          metaInfoLabels={metaInfoLabels}
          actions={actions}
          status={firm.status}
          errors={<GQLExtensionErrorAlert title="Failed to create Investor Firm" error={error} />}
        />
      }
      gridContent={
        <Fragment>
          <FirmDetailsSection tenantId={crmIntegrationId} firm={firm} />
          <ContactInformationSection tenantId={crmIntegrationId} firm={firm} />
          <CMRInvestorFirmDetailGridsRoute
            crmIntegrationId={crmIntegrationId}
            firmStatus={firm.status}
            investorContactsCount={firm.investorContactsCount}
            salesCoverageCount={firm.salesCoverageCount}
          />
        </Fragment>
      }
    />
  );
}
